import { useState, type FormEvent } from 'react';
import { patchMyTutorProfile } from '@/lib/tutor-api';
import {
  EDUCATOR_SUBJECTS,
  GRADE_LEVELS,
  type EducatorSubject,
  type GradeLevel,
} from '@/lib/schemas/tutor-profile';
import { ChipToggle, FooterButtons, type StepProps, withCompletedStep } from './shared';

export function SubjectsStep({ profile, accessToken, onSaved, onError }: StepProps) {
  const [subjects, setSubjects] = useState<EducatorSubject[]>(profile?.subjects ?? []);
  const [gradeLevels, setGradeLevels] = useState<GradeLevel[]>(profile?.gradeLevels ?? []);
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (subjects.length === 0 && gradeLevels.length === 0) {
      onError(new Error('Select at least one subject and one grade level.'));
      return;
    }
    if (subjects.length === 0) {
      onError(new Error('Please select at least one subject.'));
      return;
    }
    if (gradeLevels.length === 0) {
      onError(new Error('Please select at least one grade level.'));
      return;
    }
    setBusy(true);
    try {
      const sortedGradeLevels = [...gradeLevels].sort(
        (a, b) => GRADE_LEVELS.indexOf(a) - GRADE_LEVELS.indexOf(b)
      );
      const next = await patchMyTutorProfile(accessToken, {
        subjects,
        gradeLevels: sortedGradeLevels,
        completedSteps: withCompletedStep(profile, 'subjects'),
      });
      onSaved(next);
    } catch (err) {
      onError(err);
    } finally {
      setBusy(false);
    }
  };

  const toggle = <T extends string>(value: T, list: T[], setter: (v: T[]) => void) => {
    setter(list.includes(value) ? list.filter((v) => v !== value) : [...list, value]);
  };

  return (
    <form className="space-y-6" onSubmit={submit}>
      <fieldset>
        <legend className="mb-1 text-sm font-semibold text-foreground">Subjects you teach</legend>
        <p className="mb-3 text-xs text-muted-foreground">Select all that apply</p>
        <div className="flex flex-wrap gap-2">
          {EDUCATOR_SUBJECTS.map((s) => (
            <ChipToggle
              key={s}
              label={s}
              selected={subjects.includes(s)}
              onToggle={() => toggle(s, subjects, setSubjects)}
            />
          ))}
        </div>
      </fieldset>

      <fieldset>
        <legend className="mb-1 text-sm font-semibold text-foreground">Grade levels</legend>
        <p className="mb-3 text-xs text-muted-foreground">Select all you're comfortable teaching</p>
        <div className="flex flex-wrap gap-2">
          {GRADE_LEVELS.map((g) => (
            <ChipToggle
              key={g}
              label={g}
              selected={gradeLevels.includes(g)}
              onToggle={() => toggle(g, gradeLevels, setGradeLevels)}
            />
          ))}
        </div>
      </fieldset>

      <FooterButtons primaryLabel="Save & continue" busy={busy} />
    </form>
  );
}
